import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import TreeViewList from './Components/TreeViewList';
import TreeViewSelected from './Components/TreeViewSelected';
import TaxonomyLeydenTree from './Components/TaxonomyLeydenTree';
import { SELECTED_PANEL_HEADING_ID } from './TreeViewConstants';
import './TreeView.scss';

function TreeView({
  taxonomies,
  selectedTaxonomies,
  selectedListExpanded,
  selectedCustomItems,
  toggleSelectedList,
  toggleTaxonomy,
  loadTaxonomy,
  selectTaxonomy,
  removeTaxonomy,
  onReset,
  init,
  showCountIndicator,
  ariaLabel,
  layout = 'inline',
  initialQuery = null,
  onClearSelected,
  treeControls,
}) {
  useEffect(() => {
    init();

    return () => {
      onReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleViewList = (taxonomy) => {
    if (!taxonomy.isFolder) {
      return;
    }
    if (taxonomy.children.length) {
      toggleTaxonomy(taxonomy);
      return;
    }
    loadTaxonomy(taxonomy);
  };

  if (layout === 'sidebar') {
    return (
      <div className="treeView treeView--sidebar">
        <div className="treeView__tree">
          {treeControls && <div className="treeView__treeControls">{treeControls}</div>}
          {taxonomies.length ? (
            <TaxonomyLeydenTree
              taxonomies={taxonomies}
              query={initialQuery?.text}
              onSelect={selectTaxonomy}
              onLoad={onToggleViewList}
              showCountIndicator={showCountIndicator}
              ariaLabel={ariaLabel}
              id="taxonomy-lookup-tree"
            />
          ) : null}
        </div>

        {/*
          Named by its own visible heading rather than a hardcoded aria-label, so
          the landmark name cannot drift from what is on screen — the same pattern
          as the results filter lookup dialog's selected panel. `region` (section
          with an accessible name) rather than `complementary` (aside): the panel
          is a part of this dialog's content, not content complementary to the
          page.
        */}
        <section className="treeView__selected" aria-labelledby={SELECTED_PANEL_HEADING_ID}>
          <TreeViewSelected
            selectedListExpanded={selectedListExpanded}
            selectedTaxonomies={selectedTaxonomies}
            onToggle={toggleSelectedList}
            onRemove={removeTaxonomy}
            selectedCustomItems={selectedCustomItems}
            onClear={onClearSelected}
            layout="sidebar"
          />
        </section>
      </div>
    );
  }

  return (
    <div className="treeView">
      {!selectedListExpanded && taxonomies.length ? (
        <TreeViewList
          taxonomies={taxonomies}
          onToggle={onToggleViewList}
          onSelect={selectTaxonomy}
          showCountIndicator={showCountIndicator}
          ariaLabel={ariaLabel}
        />
      ) : null}

      {selectedTaxonomies.length || selectedCustomItems.length ? (
        <div className="treeView__selected">
          <TreeViewSelected
            selectedListExpanded={selectedListExpanded}
            selectedTaxonomies={selectedTaxonomies}
            onToggle={toggleSelectedList}
            onRemove={removeTaxonomy}
            selectedCustomItems={selectedCustomItems}
            onClear={onClearSelected}
            layout="inline"
          />
        </div>
      ) : null}
    </div>
  );
}

TreeView.propTypes = {
  showCountIndicator: PropTypes.bool,
  taxonomies: PropTypes.array,
  selectedTaxonomies: PropTypes.array,
  selectedCustomItems: PropTypes.array,
  selectedListExpanded: PropTypes.bool,
  toggleSelectedList: PropTypes.func.isRequired,
  toggleTaxonomy: PropTypes.func.isRequired,
  loadTaxonomy: PropTypes.func.isRequired,
  selectTaxonomy: PropTypes.func.isRequired,
  removeTaxonomy: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  init: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  layout: PropTypes.oneOf(['inline', 'sidebar']),
  initialQuery: PropTypes.shape({ text: PropTypes.string }),
  onClearSelected: PropTypes.func,
  treeControls: PropTypes.node,
};

export default TreeView;
